import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';

const ContestRulesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-primary px-4 pb-28 pt-8 text-gray-200 font-body md:px-8 md:pb-12 md:pt-12">
      <SEO
        title="Contest Rules | Fantasy Futbol"
        description="Official rules for Fantasy Futbol daily and weekend contests. All contests are free to enter and award no prizes."
        path="/contest-rules"
      />

      <main className="mx-auto max-w-2xl">
        <article className="rounded-xl border border-gray-700/60 bg-surface-card px-5 py-6 shadow-lg shadow-black/10 md:px-8 md:py-8">
          <header className="border-b border-gray-700/60 pb-5">
            <h1 className="text-2xl font-bold text-accent-yellow font-heading md:text-3xl">
              Contest Rules
            </h1>
            <p className="mt-1 text-sm text-gray-400">Effective August 10, 2026</p>
            <p className="mt-4 text-sm leading-6 text-gray-200 md:text-base">
              These rules apply to the free daily and weekend contests in the Contests section. Season-long leagues use their own league settings.
            </p>
          </header>

          <div className="space-y-7 pt-6 text-sm leading-6 md:text-base md:leading-7">
            <section aria-labelledby="free-to-enter">
              <h2 id="free-to-enter" className="text-lg font-bold text-white font-heading">
                1. Free to enter
              </h2>
              <p className="mt-2 text-gray-300">
                Every contest is free. No purchase or payment is required. There are no cash prizes, merchandise, credits, or anything else of monetary value. Results are for fun and leaderboard recognition only.
              </p>
            </section>

            <section aria-labelledby="who-can-play">
              <h2 id="who-can-play" className="text-lg font-bold text-white font-heading">
                2. Who can play
              </h2>
              <p className="mt-2 text-gray-300">
                You need a Fantasy Futbol account. Fantasy Futbol is not intended for children under 13. You may submit one lineup per account in each contest. Do not use more than one account to enter the same contest.
              </p>
            </section>

            <section aria-labelledby="lineup-rules">
              <h2 id="lineup-rules" className="text-lg font-bold text-white font-heading">
                3. Building your lineup
              </h2>
              <p className="mt-2 text-gray-300">
                A lineup has eight different players: two forwards, two midfielders, two defenders, and two flex players. A flex player may be a forward, midfielder, defender, or goalkeeper.
              </p>
              <p className="mt-3 text-gray-300">
                Your lineup must stay under the salary cap and use players who are eligible for that contest. The contest and lineup screens show the player pool, salary cap, matches, and start times. Your latest lineup accepted by the app is your entry.
              </p>
            </section>

            <section aria-labelledby="lineup-locks">
              <h2 id="lineup-locks" className="text-lg font-bold text-white font-heading">
                4. Lineup locks
              </h2>
              <p className="mt-2 text-gray-300">
                A player locks when that player&apos;s match starts. You may keep editing players whose matches have not started. A locked player cannot be added, removed, or moved to another lineup slot.
              </p>
            </section>

            <section aria-labelledby="scoring-results">
              <h2 id="scoring-results" className="text-lg font-bold text-white font-heading">
                5. Scoring and results
              </h2>
              <p className="mt-2 text-gray-300">
                Fantasy points follow the scoring system shown in How to Play. Live scores and ranks are unofficial. They may change as match data is updated or corrected. The lineup with the highest final score ranks first. Lineups with the same final score share the same rank.
              </p>
            </section>

            <section aria-labelledby="match-changes">
              <h2 id="match-changes" className="text-lg font-bold text-white font-heading">
                6. Match and data changes
              </h2>
              <p className="mt-2 text-gray-300">
                If a match is postponed, cancelled, abandoned, or has incomplete data, Fantasy Futbol may wait to finalize the contest or cancel it. The same decision will apply to every entry in that contest. Fantasy Futbol may also correct a clear scoring or data error before or after results first appear final.
              </p>
            </section>

            <section aria-labelledby="fair-play">
              <h2 id="fair-play" className="text-lg font-bold text-white font-heading">
                7. Fair play
              </h2>
              <p className="mt-2 text-gray-300">
                Do not use multiple accounts, bots, scripts, exploits, or coordinated entries to manipulate a contest. Fantasy Futbol may remove a lineup, correct a result, or suspend an account when needed to protect fair play.
              </p>
            </section>

            <section aria-labelledby="rule-changes">
              <h2 id="rule-changes" className="text-lg font-bold text-white font-heading">
                8. Rule changes
              </h2>
              <p className="mt-2 text-gray-300">
                Fantasy Futbol may update these rules. If a change is important, the effective date at the top of this page will be updated. A rule change will not change an already final contest unless it is needed to correct a clear error.
              </p>
            </section>

            <section aria-labelledby="apple-disclaimer">
              <h2 id="apple-disclaimer" className="text-lg font-bold text-white font-heading">
                9. Apple disclaimer
              </h2>
              <p className="mt-2 text-gray-300">
                Fantasy Futbol runs these contests. Apple is not a sponsor and is not involved in running, scoring, or administering them.
              </p>
            </section>

            <section aria-labelledby="questions">
              <h2 id="questions" className="text-lg font-bold text-white font-heading">
                Questions
              </h2>
              <p className="mt-2 text-gray-300">
                Email <a href="mailto:support@fantasy.futbol" className="text-accent-light-blue underline decoration-accent-light-blue/50 underline-offset-2 hover:text-white">support@fantasy.futbol</a>.
              </p>
            </section>
          </div>
        </article>

        <nav aria-label="Legal and support links" className="mt-8 flex min-h-8 flex-wrap items-center justify-center gap-x-3 text-xs text-gray-500">
          <Link to="/contests" className="flex min-h-8 items-center hover:text-white">Contests</Link>
          <span aria-hidden="true">·</span>
          <Link to="/privacy" state={{ from: '/contest-rules' }} className="flex min-h-8 items-center hover:text-white">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/support" state={{ from: '/contest-rules' }} className="flex min-h-8 items-center hover:text-white">Support</Link>
        </nav>
      </main>
    </div>
  );
};

export default ContestRulesPage;
